import ContentPasteSearchOutlinedIcon from "@mui/icons-material/ContentPasteSearchOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useFreelancerContext } from "../../contexts/useFreelancerContext";
import { useNavigationInterceptorContext } from "../../contexts/useNavigationInterceptorContext";
import { useSnackbarContext } from "../../contexts/useSnackBarContext";
import { useFreelancerOnboardingFormSave } from "../../hooks/useFreelancerOnboardingFormsSave";
import { useInvalidMissionEndDateValidation } from "../../hooks/useInvalidMissionEndDateValidation";
import {
	FREELANCER_PROFILE_ENTITY,
	PROFESSIONAL_EXPERIENCE_ENTITY,
	usePresentationStepContentInAnyLanguage
} from "../../hooks/usePresentationStepContentInAnyLanguage";
import { useProjectStatuses } from "../../hooks/useProject";
import { FreelancerOnboardingFormStep } from "../../models/enums/FreelancerOnboardingFormStep";
import { FreelancerOnboardingStatus } from "../../models/enums/FreelancerOnboardingStatus";
import { User } from "../../models/User";
import { lowerFirstCharacter } from "../../utils/string";
import { generateProjectStatusMenuItems } from "../../utils/menuHelpers";
import { UserInformationViewModel } from "../../ViewModels/UserInformationViewModel";
import Menu, { MenuItem } from "./Menu";
import MenuSkeleton from "./MenuSkeleton";
import { UserType } from "../../models/UserType";
import { UserRole } from "../../models/UserRole";

const getMenuItemFromRoute = (
	pathname: string,
	currentOnboardingStep: FreelancerOnboardingFormStep,
	search: string = ""
) => {
	if (pathname.startsWith("/profile")) {
		return FreelancerOnboardingFormStep[currentOnboardingStep];
	} else if (pathname.startsWith("/my-application")) {
		return "myApplication";
	} else if (pathname.match(/^\/projects\/\d+$/)) {
		// If we're on a project detail page, read status from URL params
		const params = new URLSearchParams(search);
		const status = params.get("status");
		if (status) {
			return `${status.toLowerCase()}Projects`;
		}
		return "allProjects";
	} else if (pathname === "/projects") {
		// On projects list, determine active item from URL params
		const params = new URLSearchParams(search);
		const status = params.get("status");
		if (status) {
			return `${status.toLowerCase()}Projects`;
		}
		return "allProjects";
	}

	return "";
};

interface Props {
	isCollapsed: boolean;
	setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
	drawerWidth: number;
	user: User;
}

const FreelancerMenu: React.FC<Props> = ({
	isCollapsed,
	setIsCollapsed,
	drawerWidth,
	user
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const [userInformationViewModel, setUserInformationViewModel] =
		useState<UserInformationViewModel>({
			userId: user.id,
			firstName: user.firstName,
			lastName: user.lastName,
			type: user.roles?.includes(UserRole.TAM)
				? t(`roles.tam`)
				: t(`enums.userType.${user.type}`),
			profilePhotoUrl: ""
		});
	const {
		freelancerOnboardingMyExpertiseStepViewModel,
		freelancerOnboardingMySkillsStepViewModel,
		freelancerOnboardingMySectorsStepViewModel,
		freelancerOnboardingMyPresentationStepViewModel,
		freelancerOnboardingMyAvailabilityStepViewModel,
		freelancerOnboardingMyProfessionalInformationStepViewModel,
		currentOnboardingFormStep: currentOnboardingStep,
		setNextOnboardingFormStep: setNextOnboardingStep,
		freelancerQueryResult,
		freelancerProfileQueryResult
	} = useFreelancerContext();
	const { data: freelancerProfile, isLoading: isFreelancerProfileLoading } =
		freelancerProfileQueryResult;

	const { data: freelancer } = freelancerQueryResult;
	const isFilledInAnyLanguage = usePresentationStepContentInAnyLanguage(freelancerProfile);
	const { data: projectStatuses } = useProjectStatuses();
	const { interceptNavigation } = useNavigationInterceptorContext();
	const { showError } = useSnackbarContext();
	const { saveOnboardingFormAsync, saveOnboardingFormStepAsync, isSaveProgressPending } =
		useFreelancerOnboardingFormSave({
			hideAllSnackbars: true
		});
	const { hasInvalidMissionEndDate, displayInvalidDateDialog } =
		useInvalidMissionEndDateValidation();
	const [isStepClickable, setIsStepClickable] = useState<{
		[key in FreelancerOnboardingFormStep]: boolean;
	}>({
		[FreelancerOnboardingFormStep.MyProfile]: true,
		[FreelancerOnboardingFormStep.MyExpertise]: false,
		[FreelancerOnboardingFormStep.MySkills]: false,
		[FreelancerOnboardingFormStep.MySectors]: false,
		[FreelancerOnboardingFormStep.MyPresentation]: false,
		[FreelancerOnboardingFormStep.MyAvailability]: false,
		[FreelancerOnboardingFormStep.MyProfessionalInformation]: false,
		[FreelancerOnboardingFormStep.MyPersonalInformation]: false
	});

	const [activeMenuItem, setActiveMenuItem] = useState<string>(() => {
		// Initialize with a fallback value, will be updated when freelancerProfile loads
		return FreelancerOnboardingFormStep[FreelancerOnboardingFormStep.MyProfile];
	});

	const getInitialActiveMenuItem = useCallback(() => {
		const routeBasedItem = getMenuItemFromRoute(
			location.pathname,
			currentOnboardingStep,
			location.search
		);
		const storageKey = `zoulloo_freelancer_menu_active_item_user_${user.id}`;
		const storedItem = sessionStorage.getItem(storageKey);

		if (!storedItem || routeBasedItem !== storedItem) {
			sessionStorage.setItem(storageKey, routeBasedItem);

			return routeBasedItem;
		}

		return storedItem;
	}, [
		location.pathname,
		location.search,
		user.id,
		freelancerProfile?.onboarding.currentStatus,
		currentOnboardingStep
	]);

	const navigateToProfile = (step: FreelancerOnboardingFormStep) => {
		const stepKey = FreelancerOnboardingFormStep[step];
		const storageKey = `zoulloo_freelancer_menu_active_item_user_${user.id}`;

		sessionStorage.setItem(storageKey, stepKey);
		setActiveMenuItem(stepKey);
		if (user.roles?.includes(UserRole.TAM)) {
			// Extract the key from the URL path since this component is outside the route context
			const pathMatch = location.pathname.match(/^\/profile\/([^/]+)/);
			const freelancerKey = pathMatch ? pathMatch[1] : undefined;

			navigate(`/profile/${freelancerKey}`);
		} else if (
			freelancerProfile?.onboarding.currentStatus === FreelancerOnboardingStatus.Accepted
		) {
			navigate("/profile");
		} else {
			navigate("/profile/onboarding");
		}
	};

	// Helper function to handle submenu item navigation
	const handleSubmenuNavigation = useCallback(
		(step: FreelancerOnboardingFormStep) => async () => {
			if (hasInvalidMissionEndDate) {
				displayInvalidDateDialog(step);
				// Reject so the Menu reverts its optimistic highlight: the navigation is
				// deferred to the dialog, and a cancel must not leave the clicked step
				// highlighted while the page stayed put.
				throw new Error("Navigation deferred to the invalid mission end date dialog");
			}

			if (
				freelancerProfile?.onboarding.currentStatus ===
					FreelancerOnboardingStatus.Accepted ||
				(location.pathname !== "/profile/onboarding" && location.pathname !== "/profile")
			) {
				try {
					await saveOnboardingFormStepAsync(step);
				} catch (error) {
					// The save failed: show the feedback here (the hooks run with
					// hideAllSnackbars) and rethrow so the Menu reverts its optimistic
					// highlight — the navigation did not happen.
					showError("freelancerOnboardingFormsProgressUpdate.updateError");
					throw error;
				}
				navigateToProfile(step);
				return;
			}

			await interceptNavigation({
				onValidNavigation: async () => {
					try {
						await saveOnboardingFormAsync(step);
					} catch (error) {
						showError("freelancerOnboardingFormsProgressUpdate.updateError");
						throw error;
					}
					navigateToProfile(step);
				},
				onBlockedNavigation: () => {
					setNextOnboardingStep(step);
				}
			});
		},
		[
			user.id,
			interceptNavigation,
			saveOnboardingFormAsync,
			saveOnboardingFormStepAsync,
			setActiveMenuItem,
			navigate,
			setNextOnboardingStep,
			showError,
			hasInvalidMissionEndDate,
			displayInvalidDateDialog,
			freelancerProfile,
			location.pathname
		]
	);

	// Helper function to handle menu item click and update session storage
	const handleMenuItemClick = useCallback(
		(menuKey: string, navigationFunction: () => void) => {
			const storageKey = `zoulloo_freelancer_menu_active_item_user_${user.id}`;
			sessionStorage.setItem(storageKey, menuKey);
			setActiveMenuItem(menuKey);
			navigationFunction();
		},
		[user.id, setActiveMenuItem]
	);

	// Helper function to create submenu items
	const createSubmenuItems = useCallback((): MenuItem[] => {
		// Get all onboarding steps except MyProfile
		const allSteps = Object.values(FreelancerOnboardingFormStep);
		const submenuSteps = allSteps.filter(
			(value): value is FreelancerOnboardingFormStep =>
				typeof value !== "string" && value !== FreelancerOnboardingFormStep.MyProfile
		);

		// Create menu item for each step
		return submenuSteps.map(step => {
			const stepKey = FreelancerOnboardingFormStep[step];
			const stepText = t(`menu.${lowerFirstCharacter(stepKey)}`);
			const isDisabled =
				(!isStepClickable[step] &&
					freelancerProfile?.onboarding.currentStatus !==
						FreelancerOnboardingStatus.Accepted) ||
				isSaveProgressPending;

			return {
				key: stepKey,
				text: stepText,
				disableClick: isDisabled,
				onClick: handleSubmenuNavigation(step)
			} as MenuItem;
		});
	}, [isStepClickable, isSaveProgressPending, t, handleSubmenuNavigation]);

	// Function to create all menu items
	const createMenuItems = useCallback((): MenuItem[] => {
		const menuItems: MenuItem[] = [];

		const currentStatus = freelancerProfile?.onboarding.currentStatus;
		const hasCompletedEvaluation =
			freelancerProfile?.onboarding.freelancerOnboardingExperienceRatingId &&
			freelancerProfile.onboarding.freelancerOnboardingExperienceRatingId > 0;

		// Show profile menu only if:
		// - User is TAM (always show for TAMs)
		// - NOT accepted yet (still in onboarding process)
		// - OR accepted AND evaluation completed (full access)
		const isTam = user.roles?.includes(UserRole.TAM) || false;
		const showProfileMenu =
			isTam ||
			currentStatus !== FreelancerOnboardingStatus.Accepted ||
			(currentStatus === FreelancerOnboardingStatus.Accepted && hasCompletedEvaluation);

		if (showProfileMenu) {
			menuItems.push({
				key: FreelancerOnboardingFormStep[FreelancerOnboardingFormStep.MyProfile],
				text: t(
					`menu.${lowerFirstCharacter(
						FreelancerOnboardingFormStep[FreelancerOnboardingFormStep.MyProfile]
					)}`
				),
				icon: <PermIdentityOutlinedIcon fontSize="medium" />,
				disableClick:
					!isStepClickable[FreelancerOnboardingFormStep.MyProfile] ||
					isSaveProgressPending,
				onClick: undefined,
				submenu: createSubmenuItems()
			});
		}

		if (
			currentStatus !== FreelancerOnboardingStatus.NotSubmitted &&
			user.type === UserType.Freelancer
		) {
			menuItems.push({
				key: "myApplication",
				text: t("menu.myApplication"),
				icon: <ContentPasteSearchOutlinedIcon fontSize="medium" />,
				onClick: () => {
					handleMenuItemClick("myApplication", () => navigate("/my-application"));
				}
			});
		}

		// Generate status menu items dynamically
		const statusMenuItems = projectStatuses
			? generateProjectStatusMenuItems(projectStatuses, t, navigate, "/projects")
			: [];

		// Show "My Projects" only if accepted AND evaluation is completed
		if (
			currentStatus === FreelancerOnboardingStatus.Accepted &&
			hasCompletedEvaluation &&
			user.type === UserType.Freelancer
		) {
			menuItems.push({
				key: "myProjects",
				text: t("menu.myProjects"),
				icon: <FolderOutlinedIcon fontSize="medium" />,
				onClick: undefined,
				submenu: statusMenuItems
			});
		}

		return menuItems;
	}, [
		freelancerProfile,
		isStepClickable,
		isSaveProgressPending,
		t,
		createSubmenuItems,
		handleMenuItemClick,
		navigate,
		projectStatuses
	]);

	// Memoized menu items - recalculates only when dependencies change
	const mainMenuItems = useMemo(() => createMenuItems(), [createMenuItems]);

	useEffect(() => {
		setUserInformationViewModel({
			userId: user.id,
			firstName: user.firstName,
			lastName: user.lastName,
			type: user.roles?.includes(UserRole.TAM)
				? t(`roles.tam`)
				: t(`enums.userType.${user.type}`),
			profilePhotoUrl:
				user.type === UserType.Freelancer
					? `${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME}/${freelancer?.picture?.urlPath}`
					: ""
		});
	}, [user, freelancer]);

	useEffect(() => {
		if (!freelancerProfile) return;

		setIsStepClickable({
			[FreelancerOnboardingFormStep.MyProfile]: true,
			[FreelancerOnboardingFormStep.MyExpertise]:
				freelancerProfile.onboarding.formsProgress.lastReachedStep >=
				FreelancerOnboardingFormStep.MyExpertise,
			[FreelancerOnboardingFormStep.MySkills]: getIsMyExpertiseOnboardingStepValid(),
			[FreelancerOnboardingFormStep.MySectors]:
				getIsMyExpertiseOnboardingStepValid() && getIsMySkillsOnboardingStepValid(),
			[FreelancerOnboardingFormStep.MyPresentation]:
				getIsMyExpertiseOnboardingStepValid() &&
				getIsMySkillsOnboardingStepValid() &&
				getIsMySectorsOnboardingStepValid(),
			[FreelancerOnboardingFormStep.MyAvailability]: !!(
				getIsMyExpertiseOnboardingStepValid() &&
				getIsMySkillsOnboardingStepValid() &&
				getIsMySectorsOnboardingStepValid() &&
				getIsMyPresentationOnboardingStepValid()
			),
			[FreelancerOnboardingFormStep.MyProfessionalInformation]: !!(
				getIsMyExpertiseOnboardingStepValid() &&
				getIsMySkillsOnboardingStepValid() &&
				getIsMySectorsOnboardingStepValid() &&
				getIsMyPresentationOnboardingStepValid() &&
				getIsMyAvailabilityOnboardingStepValid()
			),
			[FreelancerOnboardingFormStep.MyPersonalInformation]: !!(
				getIsMyExpertiseOnboardingStepValid() &&
				getIsMySkillsOnboardingStepValid() &&
				getIsMySectorsOnboardingStepValid() &&
				getIsMyPresentationOnboardingStepValid() &&
				getIsMyAvailabilityOnboardingStepValid() &&
				getIsMyProfessionalInformationOnboardingStepValid()
			)
		});
	}, [
		freelancerProfile,
		freelancerOnboardingMyExpertiseStepViewModel,
		freelancerOnboardingMySkillsStepViewModel,
		freelancerOnboardingMySectorsStepViewModel,
		freelancerOnboardingMyPresentationStepViewModel,
		freelancerOnboardingMyAvailabilityStepViewModel,
		freelancerOnboardingMyProfessionalInformationStepViewModel,
		isFilledInAnyLanguage
	]);

	// Initialize activeMenuItem when freelancerProfile is loaded
	useEffect(() => {
		if (!freelancerProfile) return;

		const initialActiveItem = getInitialActiveMenuItem();

		setActiveMenuItem(initialActiveItem);
	}, [
		freelancerProfile,
		currentOnboardingStep,
		location.pathname,
		getInitialActiveMenuItem
	]);

	// Listen for route changes and update active menu item (for status filtering)
	useEffect(() => {
		if (!location.pathname.includes("/projects")) return;

		const newActiveItem = getMenuItemFromRoute(
			location.pathname,
			currentOnboardingStep,
			location.search
		);

		if (newActiveItem && newActiveItem !== activeMenuItem) {
			const storageKey = `zoulloo_freelancer_menu_active_item_user_${user.id}`;
			sessionStorage.setItem(storageKey, newActiveItem);
			setActiveMenuItem(newActiveItem);
		}
	}, [
		location.pathname,
		location.search,
		currentOnboardingStep,
		activeMenuItem,
		user.id
	]);

	if (isFreelancerProfileLoading || !freelancerProfile) {
		return <MenuSkeleton isCollapsed={isCollapsed} drawerWidth={drawerWidth} />;
	}

	const getIsMyExpertiseOnboardingStepValid = () => {
		return (
			(freelancerOnboardingMyExpertiseStepViewModel &&
				freelancerOnboardingMyExpertiseStepViewModel?.form?.isValid === true &&
				!freelancerOnboardingMyExpertiseStepViewModel?.form?.isSubmitting) ||
			(!freelancerOnboardingMyExpertiseStepViewModel &&
				freelancerProfile.expertise &&
				freelancerProfile.experience &&
				!!freelancerProfile.freelancingYearsOfExperience)
		);
	};

	const getIsMySkillsOnboardingStepValid = () => {
		return (
			freelancerProfile.onboarding.formsProgress.lastReachedStep >=
				FreelancerOnboardingFormStep.MySectors &&
			((freelancerOnboardingMySkillsStepViewModel &&
				freelancerOnboardingMySkillsStepViewModel?.form?.isValid === true &&
				!freelancerOnboardingMySkillsStepViewModel?.form?.isSubmitting) ||
				(!freelancerOnboardingMySkillsStepViewModel &&
					(freelancer?.languages.length ?? 0) > 0 &&
					freelancerProfile.regions.length > 0))
		);
	};

	const getIsMySectorsOnboardingStepValid = () => {
		return (
			freelancerProfile.onboarding.formsProgress.lastReachedStep >=
				FreelancerOnboardingFormStep.MyPresentation &&
			((freelancerOnboardingMySectorsStepViewModel &&
				freelancerOnboardingMySectorsStepViewModel?.form?.isValid === true &&
				!freelancerOnboardingMySectorsStepViewModel?.form?.isSubmitting) ||
				(!freelancerOnboardingMySectorsStepViewModel &&
					freelancerProfile.sectors.length > 0 &&
					freelancerProfile.businessDomains.length > 0))
		);
	};

	const getIsMyPresentationOnboardingStepValid = () => {
		return (
			freelancerProfile.onboarding.formsProgress.lastReachedStep >=
				FreelancerOnboardingFormStep.MyAvailability &&
			((freelancerOnboardingMyPresentationStepViewModel &&
				freelancerOnboardingMyPresentationStepViewModel?.form?.isValid === true &&
				!freelancerOnboardingMyPresentationStepViewModel?.form?.isSubmitting) ||
				(!freelancerOnboardingMyPresentationStepViewModel &&
					// Check if presentation exists and is not empty
					isFilledInAnyLanguage(
						FREELANCER_PROFILE_ENTITY,
						freelancerProfile.id,
						"presentation",
						freelancerProfile.presentation
					) &&
					// Check if there's at least one professional experience
					freelancerProfile.professionalExperiences.length > 0 &&
					// Check if all professional experiences have required fields
					freelancerProfile.professionalExperiences.every(
						exp =>
							exp.companyName &&
							exp.companyName.trim().length > 0 &&
							exp.sector &&
							isFilledInAnyLanguage(
								PROFESSIONAL_EXPERIENCE_ENTITY,
								exp.id,
								"jobTitle",
								exp.jobTitle
							) &&
							exp.startDate &&
							exp.startDate.trim().length > 0 &&
							(exp.cityId || exp.cityName) &&
							isFilledInAnyLanguage(
								PROFESSIONAL_EXPERIENCE_ENTITY,
								exp.id,
								"missionDescription",
								exp.missionDescription
							) &&
							// If not current position, endDate is required
							(exp.isCurrentPosition || (exp.endDate && exp.endDate.trim().length > 0))
					)))
		);
	};

	const getIsMyAvailabilityOnboardingStepValid = () => {
		return (
			freelancerProfile.onboarding.formsProgress.lastReachedStep >=
				FreelancerOnboardingFormStep.MyProfessionalInformation &&
			((freelancerOnboardingMyAvailabilityStepViewModel &&
				freelancerOnboardingMyAvailabilityStepViewModel?.form?.isValid === true &&
				!freelancerOnboardingMyAvailabilityStepViewModel?.form?.isSubmitting) ||
				(!freelancerOnboardingMyAvailabilityStepViewModel &&
					freelancerProfile.availability &&
					(freelancerProfile.availability.isAvailable ||
						(!freelancerProfile.availability.isAvailable &&
							freelancerProfile.availability.missionEndDate)) &&
					(freelancerProfile.availability.isFullTime ||
						(!freelancerProfile.availability.isFullTime &&
							freelancerProfile.availability.daysPerWeek &&
							freelancerProfile.availability.daysPerWeek >= 1 &&
							freelancerProfile.availability.daysPerWeek <= 7))))
		);
	};

	const getIsMyProfessionalInformationOnboardingStepValid = () => {
		return (
			freelancerProfile.onboarding.formsProgress.lastReachedStep >=
				FreelancerOnboardingFormStep.MyPersonalInformation &&
			((freelancerOnboardingMyProfessionalInformationStepViewModel &&
				freelancerOnboardingMyProfessionalInformationStepViewModel?.form?.isValid ===
					true &&
				!freelancerOnboardingMyProfessionalInformationStepViewModel?.form
					?.isSubmitting) ||
				(!freelancerOnboardingMyProfessionalInformationStepViewModel &&
					// Check daily rate validation (optional but if provided, max should be greater than min)
					((freelancerProfile.dailyRateMin == null &&
						freelancerProfile.dailyRateMax == null) ||
						(freelancerProfile.dailyRateMin != null &&
							freelancerProfile.dailyRateMax != null &&
							freelancerProfile.dailyRateMin >= 0 &&
							freelancerProfile.dailyRateMax >= 0 &&
							freelancerProfile.dailyRateMax > freelancerProfile.dailyRateMin) ||
						(freelancerProfile.dailyRateMin == null &&
							freelancerProfile.dailyRateMax != null &&
							freelancerProfile.dailyRateMax >= 0) ||
						(freelancerProfile.dailyRateMin != null &&
							freelancerProfile.dailyRateMax == null &&
							freelancerProfile.dailyRateMin >= 0)) &&
					// Check preferred mission duration is required
					freelancerProfile.missionsPreferredDuration &&
					// Check preferred location is required
					freelancerProfile.jobPreferredLocation &&
					// Check company information based on status
					freelancerProfile.company &&
					// Validate company data based on the status type
					(freelancerProfile.company.isPortageSalarial === true ||
						freelancerProfile.company.isPortageSalarial === false) &&
					// Check required company fields
					freelancerProfile.company.countryId &&
					freelancerProfile.company.country &&
					freelancerProfile.company.address?.fullAddress &&
					freelancerProfile.company.identificationNumber &&
					freelancerProfile.company.identificationNumber.trim().length > 0 &&
					freelancerProfile.company.legalName &&
					freelancerProfile.company.legalName.trim().length >= 2 &&
					freelancerProfile.company.contactFirstName &&
					freelancerProfile.company.contactFirstName.trim().length > 0 &&
					freelancerProfile.company.contactLastName &&
					freelancerProfile.company.contactLastName.trim().length > 0 &&
					freelancerProfile.company.contactEmail &&
					/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
						freelancerProfile.company.contactEmail
					) &&
					// Phone number is optional but if provided should be valid format
					(!freelancerProfile.company.contactPhoneNumber ||
						freelancerProfile.company.contactPhoneNumber.trim() === "" ||
						freelancerProfile.company.contactPhoneNumber.length > 4) &&
					freelancerProfile.company.areCompanyInformationAcknowledged))
		);
	};

	return (
		<Menu
			variant="permanent"
			isCollapsed={isCollapsed}
			setIsCollapsed={setIsCollapsed}
			drawerWidth={drawerWidth}
			defaultActiveItem={activeMenuItem}
			userInformationViewModel={userInformationViewModel}
			lastActiveMenuStorageKey={`zoulloo_freelancer_menu_active_item_user_${user.id}`}
			mainMenuItems={mainMenuItems}
		/>
	);
};

export default FreelancerMenu;
